import { Component, OnInit } from '@angular/core';
import { LayoutService } from '@common/services/layout.service';
import { DatePickerHandler } from '@common/UI/date-picker/date-picker.component';
import { Filterable } from '@common/utilities/Filterable';
import { commit, main } from '@core/operators';
import { ApiService } from '@core/services/api.service';
import { DataService, DateRange, PriceReport, ProductUnit } from '@core/services/data.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { map } from 'rxjs/operators';
import { Report } from '../common/report';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';

interface PriceReportEx extends PriceReport {
  ImportUnitPriceTrend: 'positive' | 'negative' | 'none';
  ExportUnitPriceTrend: 'positive' | 'negative' | 'none';
  ConversionTrend: 'positive' | 'negative' | 'none';
}


interface Good {
  name: string;
  SKU: string;
  Colors: string[];
  ImportUnitPriceChanges: number;
  ImportUnitPriceRange: { from: number, to: number };
  ExportUnitPriceChanges: number;
  ExportUnitPriceRange: { from: number, to: number };
  ConversionChanges: number;
  ConversionRange: { from: number, to: number };
  records: PriceReport[];
  children: Good[];
}

@Component({
  templateUrl: './price-report-by-products.component.html',
  styleUrls: ['./price-report-by-products.component.scss']
})
export class PriceReportByProductsComponent implements OnInit {

  constructor(private ds: DataService, private layout: LayoutService, private sync: SynchronizationService) {
  }

  public get loading(): boolean {
    return !this.sync.ready(PriceReportByProductsComponent);
  }

  public paths: Good[] = [];
  public good: Good;

  public range: { name: string, range: DateRange };

  public goods = new Filterable<Good>({
    extractor: i => [i.SKU],
    grouper: i => [
      { name: Multilingual.getText('Giá nhập'), option: Multilingual.getText(i.ImportUnitPriceChanges < 1 ? 'Không biến động' : 'Có biến động') },
      { name: Multilingual.getText('Giá xuất'), option: Multilingual.getText(i.ExportUnitPriceChanges < 1 ? 'Không biến động' : 'Có biến động') },
      { name: Multilingual.getText('Tỷ giá'), option: Multilingual.getText(i.ConversionChanges < 1 ? 'Không biến động' : 'Có biến động') }
    ]
  });

  public details = new Filterable<PriceReportEx>({
    extractor: i => [i.Customer],
    grouper: i => [{ name: Multilingual.getText('Khách hàng'), option: i.Customer }]
  });

  public handler: DatePickerHandler = {

    change: (info: { name: string, range: DateRange }) => {

      this.range = info;

      this.sync.notify(PriceReportByProductsComponent, false);

      return this.ds.reports('price-by-products', { range: info.range }).pipe(map(data => data && data.Prices
        ? commit<boolean>(() => {

          data.Prices = data.Prices.order(x => x.Stamp, 'descending').map<PriceReportEx>((x,ix, items) => ({
            ...x,
            ImportUnitPriceTrend: (ix + 1 == items.length || x.ImportUnitPrice == items[ix + 1].ImportUnitPrice) ? 'none' : x.ImportUnitPrice > items[ix + 1].ImportUnitPrice ? 'negative' : 'positive',
            ExportUnitPriceTrend: (ix + 1 == items.length || x.ExportUnitPrice == items[ix + 1].ExportUnitPrice) ? 'none' : x.ExportUnitPrice < items[ix + 1].ExportUnitPrice ? 'negative' : 'positive',
            ConversionTrend: (ix + 1 == items.length || x.Conversion == items[ix + 1].Conversion) ? 'none' : x.Conversion > items[ix + 1].Conversion ? 'negative' : 'positive',
          }));

          let root: Good = {
            name: Multilingual.getText('Các mã có đặt hàng'),
            SKU: '',
            Colors: [],
            ImportUnitPriceChanges: 0,
            ImportUnitPriceRange: null,
            ExportUnitPriceChanges: 0,
            ExportUnitPriceRange: null,
            ConversionChanges: 0,
            ConversionRange: null,

            records: data.Prices,

            children: data.Prices.group(i => i.SKU).map<Good>(sku => ({

              name: sku.key as string,
              SKU: sku.key as string,
              Colors: sku.items.map(i => i.Color).distinct(),
              ImportUnitPriceChanges: sku.items.group(i => i.ImportUnitPrice).length - 1,
              ImportUnitPriceRange: { from: sku.items.min(i => i.ImportUnitPrice), to: sku.items.max(i => i.ImportUnitPrice) },
              ExportUnitPriceChanges: sku.items.group(i => i.ExportUnitPrice).length - 1,
              ExportUnitPriceRange: { from: sku.items.min(i => i.ExportUnitPrice), to: sku.items.max(i => i.ExportUnitPrice) },
              ConversionChanges: sku.items.group(i => i.Conversion).length - 1,
              ConversionRange: { from: sku.items.min(i => i.Conversion), to: sku.items.max(i => i.Conversion) },

              records: sku.items,

              children: sku.items.group(i => i.Color).map<Good>(color => ({

                name: Multilingual.getText('màu') + ' ' + color.key,
                SKU: sku.key as string,
                Colors: [color.key as string],
                ImportUnitPriceChanges: color.items.group(i => i.ImportUnitPrice).length - 1,
                ImportUnitPriceRange: { from: color.items.min(i => i.ImportUnitPrice), to: color.items.max(i => i.ImportUnitPrice) },
                ExportUnitPriceChanges: color.items.group(i => i.ExportUnitPrice).length - 1,
                ExportUnitPriceRange: { from: color.items.min(i => i.ExportUnitPrice), to: color.items.max(i => i.ExportUnitPrice) },
                ConversionChanges: color.items.group(i => i.Conversion).length - 1,
                ConversionRange: { from: color.items.min(i => i.Conversion), to: color.items.max(i => i.Conversion) },

                records: color.items,
                children: []
              }))
            }))
          };

          this.open(root);
          this.sync.notify(PriceReportByProductsComponent, true);

        }).return(true)
        : false));
    }
  }

  public open(good: Good) {

    if (!this.paths.contains(good)) this.paths.push(good);
    this.paths = this.paths.take(this.paths.indexOf(good) + 1);

    this.good = good;

    if (good.children.length == 1) {
      this.open(good.children[0]);
      return;
    }

    if (good.children.length > 0) {
      this.details.source = [];
      this.goods.source = good.children;     
    }
    else {
      this.details.source = good.records as PriceReportEx[];
    }
  }

  public trend(good: Good, field: 'ImportUnitPrice' | 'ExportUnitPrice' | 'Conversion', opposite: boolean = false): ('positive' | 'negative' | 'none') {
    return good.records.last()[field] < good.records.first()[field] ? (opposite ? 'negative' : 'positive') : good.records.last()[field] > good.records.first()[field] ? (opposite ? 'positive' : 'negative') : 'none';
  }

  public extractor(field): SortableExtractor<Good> {
    return {
      extract: i => {
        switch (field) {
          case 'Count': return i.records.length;
          default: return i[field];
        }
      }
    }
  }

  ngOnInit(): void {
    main(() => this.layout.title = Multilingual.getText('Báo cáo biến động hàng hóa'));    
  }

}
